/** @jsxImportSource @emotion/react */
import { css } from '@emotion/react'
import PropTypes from 'prop-types'
import colors from '@sportninja/common/constants/appColors'
import { font } from '../css'

/**
 * FilterBar component for displaying notification filters as a segmented control
 * Matches the Figma design with flush segments that fill the full width equally
 *
 * @param {Object} props - Component props
 * @param {string} props.activeFilter - Current active filter ('all' | 'unread' | 'invitations')
 * @param {Function} props.onFilterChange - Callback when filter changes (filter: string) => void
 * @param {number} props.unreadCount - Number of unread notifications for inline display
 * @param {number} props.invitationsUnreadCount - Number of unread invitations for inline display
 * @param {Array} props.filters - Optional custom filters array for extensibility
 * @returns {JSX.Element} Segmented control filter bar component
 */
const FilterBar = ({
  activeFilter = 'all',
  onFilterChange,
  unreadCount = 0,
  invitationsUnreadCount = 0,
  filters = null,
}) => {
  console.log('🔄 FilterBar: Rendering filter bar', {
    activeFilter,
    unreadCount,
    invitationsUnreadCount,
  })

  // Default filters matching mobile implementation
  const defaultFilters = [
    { key: 'all', label: 'All' },
    { key: 'unread', label: 'Unread', showCount: true, count: unreadCount },
    {
      key: 'invitations',
      label: 'Invitations',
      showCount: true,
      count: invitationsUnreadCount,
    },
  ]

  const filterList = filters || defaultFilters

  /**
   * Get the display label for a filter
   * @param {Object} filter - Filter object
   * @returns {string} Filter label text
   */
  const getFilterLabel = (filter) => {
    return filter.label
  }

  /**
   * Format count for badge display - shows "+99" for counts over 99
   * @param {number} count - The count to format
   * @returns {string} Formatted count string
   */
  const formatCount = (count) => {
    if (count > 99) {
      return '+99'
    }
    return count.toString()
  }

  return (
    <div
      css={css`
        margin-bottom: 16px;
      `}
    >
      {/* Outer container with border and rounded corners */}
      <div
        css={css`
          display: flex;
          flex-direction: row;
          border: 1px solid ${colors.SECONDARY_100 || '#313b4b'};
          border-radius: 8px;
          overflow: hidden;
          width: 100%;
        `}
      >
        {filterList.map((filter) => {
          const isActive = activeFilter === filter.key

          return (
            <button
              key={filter.key}
              type='button'
              onClick={() => onFilterChange(filter.key)}
              css={css`
                flex: 1;
                display: flex;
                align-items: center;
                justify-content: center;
                gap: 8px;
                padding: 8px 24px;
                background-color: ${isActive
                  ? colors.SECONDARY_500 || '#1b222b'
                  : colors.SECONDARY_300 || '#27303e'};
                color: ${isActive
                  ? colors.NEUTRAL_0 || '#ffffff'
                  : colors.NEUTRAL_50 || '#c1c2c3'};
                border: none;
                ${font.body};
                font-size: 14px;
                font-weight: 500;
                line-height: 20.3px;
                cursor: pointer;
                transition: background-color 0.2s ease, color 0.2s ease;
                white-space: nowrap;
                min-width: 0;

                &:hover {
                  ${!isActive &&
                  css`
                    background-color: rgba(39, 48, 62, 0.8);
                    color: ${colors.NEUTRAL_0 || '#ffffff'};
                  `}
                }

                &:active {
                  opacity: 0.9;
                }
              `}
            >
              {getFilterLabel(filter)}
              {filter.showCount && filter.count > 0 && (
                <span
                  css={css`
                    display: flex;
                    align-items: center;
                    justify-content: center;
                    background-color: ${colors.ERROR_300 || '#d91b1b'};
                    color: ${colors.NEUTRAL_0 || '#ffffff'};
                    font-size: 10px;
                    font-weight: 400;
                    line-height: 1;
                    padding: 3px 4px;
                    border-radius: 100px;
                    min-width: 18px;
                  `}
                >
                  {formatCount(filter.count)}
                </span>
              )}
            </button>
          )
        })}
      </div>
    </div>
  )
}

FilterBar.propTypes = {
  activeFilter: PropTypes.string,
  onFilterChange: PropTypes.func.isRequired,
  unreadCount: PropTypes.number,
  invitationsUnreadCount: PropTypes.number,
  filters: PropTypes.arrayOf(
    PropTypes.shape({
      key: PropTypes.string.isRequired,
      label: PropTypes.string.isRequired,
      showCount: PropTypes.bool,
      count: PropTypes.number,
    })
  ),
}

export default FilterBar
